import { ref } from 'vue';
import type { FIELDS_COMPONENTS } from '../constants';
import type { FieldPayload, FieldInitialSettings, FieldSchema, RawField } from '../types';
import {
  AFieldCardReader,
  AFieldCheckNumber,
  AFieldCheckboxButton,
  AFieldColorSelector,
  AFieldDropdown,
  AFieldEditor,
  AFieldEmail,
  AFieldHeader,
  AFieldHexColor,
  AFieldInputText,
  AFieldMicrochipIssuer,
  AFieldMoney,
  AFieldMultiSelect,
  AFieldNumber,
  AFieldParagraph,
  AFieldPassword,
  AFieldPhone,
  AFieldPrintButton,
  AFieldRadio,
  AFieldSeparator,
  AFieldSubheader,
  AFieldSpecies,
  AFieldString,
  AFieldTextArea,
  AFieldToggle,
  AFieldWeight,
  MFieldAmountUnity,
  MFieldAnimalName,
  MFieldBooster,
  MFieldCashPayment,
  MFieldCheckbox,
  MFieldContact,
  MFieldContactType,
  MFieldCostUnity,
  MFieldCountry,
  MFieldDatePicker,
  MFieldDonationsAmount,
  MFieldFileUpload,
  MFieldGMapAutocomplete,
  MFieldGroup,
  MFieldImageUpload,
  MFieldIntakeWaivers,
  MFieldList,
  MFieldMicrochip,
  MFieldOutcomeVouchers,
  MFieldResults,
  MFieldCustomAdditionalFee,
  MFieldStaff,
  MFieldStripeCreditCard,
  MFieldTimeFrame,
  OFieldAnimalBirthday,
  OFieldFrequency,
  OFieldHoldStatus,
  OFieldKennelManager,
  OFieldSplitPayment,
} from '../components';
import { Field } from '../types';
import useFormValidations from './useFormValidations';

const removeHiddenFieldsFromCurrentSchemaModels = ref(false);
const wasFormSubmitted = ref(false);

export default function () {
  const { validateField } = useFormValidations();

  // VALIDATION
  function fieldsValidation(schema: FieldSchema[], currentSchemaModels: Set<FieldPayload>) {
    wasFormSubmitted.value = true;
    let hasErrors = false;

    currentSchemaModels.clear();

    schema.forEach((field: FieldSchema) => {
      if (!field.settings.show && removeHiddenFieldsFromCurrentSchemaModels.value) return;

      validateField(field, wasFormSubmitted.value);

      if (field.state?.errors?.size) hasErrors = true;

      currentSchemaModels.add({
        name: field.name,
        value: field.model,
        type: field.settings.type,
      });
    });

    return hasErrors;
  }

  function granularFieldValidation(field: FieldSchema) {
    validateField(field, wasFormSubmitted.value);
  }

  // GETTERS
  function getComponent(type?: FIELDS_COMPONENTS | string) {
    // prettier-ignore
    switch (type) {
      case 'AMOUNT_UNIT': return MFieldAmountUnity;
      case 'ANIMAL_BIRTHDAY': return OFieldAnimalBirthday;
      case 'ANIMAL_NAME': return MFieldAnimalName;
      case 'ANIMAL_TEMPLATE': return AFieldSpecies;
      case 'BOOSTER': return MFieldBooster;
      case 'BUTTON_CHECKBOX': return AFieldCheckboxButton;
      case 'CARD_READER': return AFieldCardReader;
      case 'CASH_PAYMENT': return MFieldCashPayment;
      case 'CHECK_NUMBER': return AFieldCheckNumber;
      case 'CHECKBOX': return MFieldCheckbox;
      case 'COLOR_SELECTOR': return AFieldColorSelector;
      case 'CONTACT': return MFieldContact;
      case 'CONTACT_TYPE': return MFieldContactType;
      case 'COST_UNIT': return MFieldCostUnity;
      case 'COUNTRY': return MFieldCountry;
      case 'DATE': return MFieldDatePicker;
      case 'DROPDOWN': return AFieldDropdown;
      case 'DONATION_AMOUNT': return MFieldDonationsAmount;
      case 'EDITOR': return AFieldEditor;
      case 'EMAIL': return AFieldEmail;
      case 'FIELD_STRING': return AFieldString;
      case 'FILE': return MFieldFileUpload;
      case 'FREQUENCY': return OFieldFrequency;
      case 'HEADER': return AFieldHeader;
      case 'HEX_COLOR': return AFieldHexColor;
      case 'HOLD_STATUS': return OFieldHoldStatus;
      case 'KENNEL_MANAGER': return OFieldKennelManager;
      case 'LIST': return MFieldList;
      case 'LOCATION': return MFieldGMapAutocomplete;
      case 'GROUP': return MFieldGroup;
      case 'IMAGE_UPLOAD': return MFieldImageUpload;
      case 'INTAKE_WAIVERS': return MFieldIntakeWaivers;
      case 'MICROCHIP': return MFieldMicrochip;
      case 'MICROCHIP_ISSUER': return AFieldMicrochipIssuer;
      case 'MONEY': return AFieldMoney;
      case 'MULTISELECT': return AFieldMultiSelect;
      case 'NUMBER': return AFieldNumber;
      case 'OUTCOME_VOUCHERS': return MFieldOutcomeVouchers;
      case 'PARAGRAPH': return AFieldParagraph;
      case 'PASSWORD': return AFieldPassword;
      case 'PRINT': return AFieldPrintButton;
      case 'RADIO': return AFieldRadio;
      case 'RESULT': return MFieldResults;
      case 'CUSTOM_ADDITIONAL_FEE':
      case 'RTO_ADDITIONAL_FEE':
        return MFieldCustomAdditionalFee;
      case 'SEPARATOR': return AFieldSeparator;
      case 'SUBHEADER': return AFieldSubheader;
      case 'SPECIES': return AFieldDropdown;
      case 'SPLIT_PAYMENT': return OFieldSplitPayment;
      case 'STAFF': return MFieldStaff;
      case 'STRIPE': return MFieldStripeCreditCard;
      case 'TEL': case 'PHONE': return AFieldPhone;
      case 'TEXT': return AFieldInputText;
      case 'TEXTAREA': return AFieldTextArea;
      case 'TIMEFRAME': return MFieldTimeFrame;
      case 'TOGGLE': return AFieldToggle;
      case 'WEIGHT': return AFieldWeight;
      default: return AFieldInputText;
    }
  }

  function getDetailsSchema(rawFields: RawField[], fieldsSettings: FieldInitialSettings, data?: any) {
    removeHiddenFieldsFromCurrentSchemaModels.value = false;
    wasFormSubmitted.value = false;

    return rawFields.map((field) => {
      const targetData = data ? data[field.name] : undefined;

      return new Field(field, fieldsSettings[field.name], targetData);
    });
  }

  // HELPERS [SETTERS]
  function setEditOptions(fieldsSettings: FieldInitialSettings) {
    if (!fieldsSettings) return null;

    // Object.keys(fieldsSettings).forEach((schemaKey: string) => {
    //   const target = fieldsSettings[schemaKey];
    //   if (!target.edit) return;

    //   const optionKey = target.edit.key;
    //   const dataHasField = result.call[schemaKey];
    //   const initialModelValue = dataHasField ? result.call[schemaKey] : '';

    //   target.edit.options = optionKey ? result[optionKey] : [];
    //   target.edit.value = dataHasField ? result.call[schemaKey] : '';
    //   target.model = initialModelValue;
    // });
  }

  function autoPopulateMicrochipIssuer(
    fieldsSchema: FieldSchema[],
    microchipValue: string | null,
    getMicrochipIssuerFn: (microchip: string) => string | null
  ) {
    if (!microchipValue) return;

    const issuer = getMicrochipIssuerFn(microchipValue);
    if (!issuer) return;

    const microchipIssuerField = fieldsSchema.find(({ name }) => name === 'microchip_issuer');
    if (microchipIssuerField) {
      microchipIssuerField.model = issuer;
      microchipIssuerField.value = issuer;
    }
  }

  return {
    autoPopulateMicrochipIssuer,
    fieldsValidation,
    getComponent,
    getDetailsSchema,
    granularFieldValidation,
    setEditOptions,
    removeHiddenFieldsFromCurrentSchemaModels,
    wasFormSubmitted,
  };
}
